import * as THREE from 'https://esm.sh/three@0.160.0';
import { RoundedBoxGeometry } from 'https://esm.sh/three@0.160.0/examples/jsm/geometries/RoundedBoxGeometry.js';
import { MarchingCubes } from 'https://esm.sh/three@0.160.0/examples/jsm/objects/MarchingCubes.js';
import { RoomEnvironment } from 'https://esm.sh/three@0.160.0/examples/jsm/environments/RoomEnvironment.js';
import { EffectComposer } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'https://esm.sh/three@0.160.0/examples/jsm/postprocessing/OutputPass.js';
import { Reflector } from 'https://esm.sh/three@0.160.0/examples/jsm/objects/Reflector.js';

// Mobile detection — same as bath
const IS_MOBILE =
  (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 900px)').matches) ||
  (typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent));

let progress = 0;
let sectionEl = null;
let canvasContainer = null;

function trackScroll() {
  if (!sectionEl) {
    sectionEl = document.querySelector('.h2-root');
    if (!sectionEl) return;
  }
  const rect = sectionEl.getBoundingClientRect();
  const winH = window.innerHeight;
  const max = Math.max(1, sectionEl.offsetHeight - winH);
  progress = Math.min(1, Math.max(0, -rect.top / max));
  if (canvasContainer) {
    const inView = rect.bottom > -100 && rect.top < winH + 100;
    const vis = inView ? 'visible' : 'hidden';
    canvasContainer.style.visibility = vis;
    const overlay = document.querySelector('.h2-text-overlay');
    if (overlay) overlay.style.visibility = vis;
    const flash = document.querySelector('.h2-flash');
    if (flash) flash.style.visibility = vis;
    const splash = document.querySelector('.h2-splash');
    if (splash) splash.style.visibility = vis;
  }
}
window.addEventListener('scroll', trackScroll, { passive: true });
window.addEventListener('resize', trackScroll);

const BG = 0x04081a;
const BLUE = 0x142b77;
const BLUE_MID = 0x066ac1;
const BLUE_LIGHT = 0x9ad9ff;
const CYAN = 0x7be0ff;

const _texLoader = new THREE.TextureLoader();
_texLoader.crossOrigin = 'anonymous';
const LOGO_URL = 'https://drwater.store/cdn/shop/files/DR.WATER-White-Logo.webp?v=1769441826&width=1200';
let _drwaterLogoTex = null;
function getLogoTex() {
  if (!_drwaterLogoTex) {
    _drwaterLogoTex = _texLoader.load(LOGO_URL);
    _drwaterLogoTex.colorSpace = THREE.SRGBColorSpace;
    _drwaterLogoTex.anisotropy = IS_MOBILE ? 1 : 4;
  }
  return _drwaterLogoTex;
}

function makeCausticsTexture() {
  const c = document.createElement('canvas'); c.width = 256; c.height = 256;
  const x = c.getContext('2d');
  x.fillStyle = '#000000'; x.fillRect(0, 0, 256, 256);
  for (let i = 0; i < 35; i++) {
    const cx = Math.random() * 256, cy = Math.random() * 256;
    const r = 12 + Math.random() * 24;
    const grad = x.createRadialGradient(cx, cy, 0, cx, cy, r);
    grad.addColorStop(0, 'rgba(255,255,255,0.55)');
    grad.addColorStop(0.4, 'rgba(180,220,255,0.18)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    x.fillStyle = grad;
    x.fillRect(cx - r, cy - r, r * 2, r * 2);
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

function makeLcdTexture() {
  const c = document.createElement('canvas'); c.width = 512; c.height = 96;
  const x = c.getContext('2d');
  const lg = x.createLinearGradient(0, 0, 512, 96);
  lg.addColorStop(0, '#3aa0d4'); lg.addColorStop(0.4, '#1e5a8a');
  lg.addColorStop(0.7, '#0d2030'); lg.addColorStop(1, '#1a3a5a');
  x.fillStyle = lg; x.fillRect(0, 0, 512, 96);
  const rg = x.createRadialGradient(140, 30, 0, 140, 30, 180);
  rg.addColorStop(0, 'rgba(168,220,255,0.55)');
  rg.addColorStop(0.5, 'rgba(168,220,255,0.15)');
  rg.addColorStop(1, 'rgba(168,220,255,0)');
  x.fillStyle = rg; x.fillRect(0, 0, 512, 96);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function buildHomeMachine() {
  const m = new THREE.Group();

  // Chrome — iridescence is expensive on mobile, skip it
  const chromeMirror = IS_MOBILE
    ? new THREE.MeshPhysicalMaterial({
        color: 0xe8e8e8, metalness: 1.0, roughness: 0.10,
        envMapIntensity: 1.4, clearcoat: 0.5
      })
    : new THREE.MeshPhysicalMaterial({
        color: 0xe8e8e8, metalness: 1.0, roughness: 0.05,
        envMapIntensity: 1.6, clearcoat: 1.0, clearcoatRoughness: 0.05,
        iridescence: 0.4, iridescenceIOR: 1.3, iridescenceThicknessRange: [100, 800]
      });
  const darkGlass = new THREE.MeshPhysicalMaterial({
    color: 0x1a1a1a, metalness: 0.0, roughness: 0.10,
    clearcoat: IS_MOBILE ? 0 : 1.0, clearcoatRoughness: 0.05, envMapIntensity: 1.0
  });
  const brushedSteel = new THREE.MeshPhysicalMaterial({
    color: 0xc8c8c8, metalness: 1.0, roughness: 0.15,
    clearcoat: IS_MOBILE ? 0 : 0.5, envMapIntensity: 1.3
  });
  const dialKnobMat = new THREE.MeshPhysicalMaterial({
    color: 0xf4f4f2, metalness: 0.30, roughness: 0.35,
    clearcoat: IS_MOBILE ? 0 : 0.6, clearcoatRoughness: 0.20
  });
  const dialIndicatorMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, metalness: 0.0, roughness: 0.5 });
  const silkscreenMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.40 });
  const rubberFoot = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.8 });
  const fuseBlue = new THREE.MeshStandardMaterial({ color: 0x1e90ff, metalness: 0.30, roughness: 0.40 });
  const switchRed = new THREE.MeshStandardMaterial({ color: 0xc0322d, metalness: 0.10, roughness: 0.50 });
  const recessDark = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, metalness: 0.10, roughness: 0.70 });
  const cableMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0a, roughness: 0.6 });

  const W = 1.28, H = 1.44, D = 0.64;
  const halfW = W / 2, halfH = H / 2, halfD = D / 2;

  const cabinet = new THREE.Mesh(new RoundedBoxGeometry(W, H, D, IS_MOBILE ? 4 : 8, 0.16), chromeMirror);
  cabinet.castShadow = !IS_MOBILE; cabinet.receiveShadow = !IS_MOBILE;
  m.add(cabinet);

  const panelW = 0.88, panelH = 1.20, panelT = 0.02;
  const panelZ = halfD - panelT * 0.5;
  const panel = new THREE.Mesh(new RoundedBoxGeometry(panelW, panelH, panelT, IS_MOBILE ? 2 : 4, 0.04), darkGlass);
  panel.position.set(0, 0, panelZ);
  m.add(panel);
  const panelFrontZ = panelZ + panelT / 2;
  const ringPlate = new THREE.Mesh(new RoundedBoxGeometry(panelW + 0.012, panelH + 0.012, 0.005, IS_MOBILE ? 2 : 4, 0.045), chromeMirror);
  ringPlate.position.set(0, 0, panelZ - 0.012);
  m.add(ringPlate);

  const lcdY = panelH * 0.36;
  const lcdTex = makeLcdTexture();
  const lcd = new THREE.Mesh(
    new THREE.PlaneGeometry(0.64, 0.10),
    new THREE.MeshStandardMaterial({
      map: lcdTex, emissiveMap: lcdTex, emissive: 0xffffff,
      emissiveIntensity: 0.6, toneMapped: false, roughness: 0.4
    })
  );
  lcd.position.set(0, lcdY, panelFrontZ + 0.005);
  m.add(lcd);

  // Bubble silkscreens — fewer on mobile
  const bubbleSpecs = IS_MOBILE
    ? [[-0.15, 0.12, 0.044], [0.10, 0.18, 0.040], [0.18, 0.05, 0.054], [-0.05, 0.04, 0.030]]
    : [[-0.15, 0.12, 0.044], [0.10, 0.18, 0.040], [0.18, 0.05, 0.054], [-0.05, 0.04, 0.030], [0.02, 0.20, 0.025], [-0.18, -0.02, 0.038]];
  bubbleSpecs.forEach(([x, y, r]) => {
    const ring = new THREE.Mesh(new THREE.RingGeometry(r * 0.85, r, IS_MOBILE ? 16 : 32), silkscreenMat);
    ring.position.set(x, y, panelFrontZ + 0.003);
    m.add(ring);
  });

  // H₂ badge
  const badgeY = -0.04;
  const badgeRing = new THREE.Mesh(new THREE.TorusGeometry(0.088, 0.004, IS_MOBILE ? 8 : 12, IS_MOBILE ? 24 : 48), brushedSteel);
  badgeRing.position.set(0, badgeY, panelFrontZ + 0.006);
  m.add(badgeRing);
  const badgeInner = new THREE.Mesh(new THREE.CircleGeometry(0.080, IS_MOBILE ? 16 : 32), darkGlass);
  badgeInner.position.set(0, badgeY, panelFrontZ + 0.004);
  m.add(badgeInner);
  const h2Canvas = document.createElement('canvas');
  h2Canvas.width = 256; h2Canvas.height = 256;
  const h2Ctx = h2Canvas.getContext('2d');
  h2Ctx.clearRect(0, 0, 256, 256);
  h2Ctx.fillStyle = '#a8d4ff';
  h2Ctx.font = 'bold 96px "Inter", "Helvetica Neue", sans-serif';
  h2Ctx.textAlign = 'center'; h2Ctx.textBaseline = 'middle';
  h2Ctx.fillText('H', 100, 116);
  h2Ctx.font = 'bold 70px "Inter", "Helvetica Neue", sans-serif';
  h2Ctx.fillText('₂', 168, 138);
  h2Ctx.font = '500 22px "Inter", "Helvetica Neue", sans-serif';
  h2Ctx.fillText('HYDROGEN', 128, 196);
  const h2Tex = new THREE.CanvasTexture(h2Canvas);
  h2Tex.colorSpace = THREE.SRGBColorSpace;
  const h2Plate = new THREE.Mesh(
    new THREE.PlaneGeometry(0.14, 0.14),
    new THREE.MeshBasicMaterial({ map: h2Tex, transparent: true, alphaTest: 0.05, depthWrite: false })
  );
  h2Plate.position.set(0, badgeY, panelFrontZ + 0.008);
  m.add(h2Plate);

  for (const x of [-0.08, 0.08]) {
    const dot = new THREE.Mesh(new THREE.RingGeometry(0.014, 0.020, IS_MOBILE ? 12 : 24), silkscreenMat);
    dot.position.set(x, badgeY - 0.18, panelFrontZ + 0.003);
    m.add(dot);
  }

  // Rotary dial
  const dialGroup = new THREE.Group();
  const dialY = -panelH * 0.30;
  dialGroup.position.set(0, dialY, panelFrontZ);
  m.add(dialGroup);
  const dialRing = new THREE.Mesh(new THREE.TorusGeometry(0.14, 0.016, IS_MOBILE ? 8 : 14, IS_MOBILE ? 24 : 48), brushedSteel);
  dialRing.position.z = 0.010;
  dialGroup.add(dialRing);
  const dialKnob = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.072, IS_MOBILE ? 24 : 64), dialKnobMat);
  dialKnob.rotation.x = Math.PI / 2;
  dialKnob.position.z = 0.044;
  dialGroup.add(dialKnob);
  const dialRotGroup = new THREE.Group();
  dialRotGroup.position.z = 0.084;
  dialGroup.add(dialRotGroup);
  const notch = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.022, 0.006), dialIndicatorMat);
  notch.position.set(0, 0.078, 0);
  dialRotGroup.add(notch);
  for (let i = 0; i < 8; i++) {
    const angle = -Math.PI * 0.75 + (i / 7) * Math.PI * 1.5;
    const tx = Math.sin(angle) * 0.165;
    const ty = Math.cos(angle) * 0.165;
    const tick = new THREE.Mesh(new THREE.BoxGeometry(0.004, 0.014, 0.002), silkscreenMat);
    tick.position.set(tx, ty, 0.005);
    tick.rotation.z = -angle;
    dialGroup.add(tick);
  }

  const wordmark = new THREE.Mesh(
    new THREE.PlaneGeometry(0.24, 0.06),
    new THREE.MeshBasicMaterial({
      map: getLogoTex(), transparent: true, opacity: 0.6,
      alphaTest: 0.05, toneMapped: false, depthWrite: false
    })
  );
  wordmark.position.set(-panelW * 0.5 + 0.14, -panelH * 0.50 + 0.04, panelFrontZ + 0.003);
  m.add(wordmark);

  const footGeom = new THREE.CylinderGeometry(0.020, 0.020, 0.032, IS_MOBILE ? 8 : 12);
  for (const fx of [-halfW + 0.08, halfW - 0.08]) {
    for (const fz of [-halfD + 0.08, halfD - 0.08]) {
      const foot = new THREE.Mesh(footGeom, rubberFoot);
      foot.position.set(fx, -halfH - 0.016, fz);
      m.add(foot);
    }
  }

  // Gooseneck — lower poly on mobile
  const spoutOrigin = new THREE.Vector3(0.20, halfH, halfD - 0.16);
  const spoutCps = [
    new THREE.Vector3(0,    0,    0),
    new THREE.Vector3(0,    0.32, 0),
    new THREE.Vector3(0,    0.60, 0.08),
    new THREE.Vector3(0.16, 0.80, 0.24),
    new THREE.Vector3(0.40, 0.72, 0.40),
    new THREE.Vector3(0.52, 0.52, 0.48),
    new THREE.Vector3(0.56, 0.40, 0.52)
  ].map(p => p.clone().add(spoutOrigin));
  const spoutCurve = new THREE.CatmullRomCurve3(spoutCps, false, 'catmullrom', 0.5);
  const spoutTube = new THREE.Mesh(new THREE.TubeGeometry(spoutCurve, IS_MOBILE ? 32 : 64, 0.032, IS_MOBILE ? 8 : 12, false), brushedSteel);
  m.add(spoutTube);
  const tipPos = spoutCps[spoutCps.length - 1];
  const tip = new THREE.Mesh(new THREE.CylinderGeometry(0.040, 0.034, 0.060, IS_MOBILE ? 10 : 16), brushedSteel);
  tip.position.copy(tipPos);
  tip.rotation.x = Math.PI / 2.2;
  m.add(tip);

  // Water flow tube
  const flowCanvas = document.createElement('canvas');
  flowCanvas.width = 64; flowCanvas.height = 16;
  const fctx = flowCanvas.getContext('2d');
  for (let i = 0; i < 64; i++) {
    const v = 0.3 + 0.7 * (Math.sin(i * 0.4) * 0.5 + 0.5);
    fctx.fillStyle = `rgba(0,212,255,${v})`;
    fctx.fillRect(i, 0, 1, 16);
  }
  const flowTex = new THREE.CanvasTexture(flowCanvas);
  flowTex.wrapS = THREE.RepeatWrapping; flowTex.wrapT = THREE.RepeatWrapping;
  flowTex.repeat.set(8, 1);
  const flowMat = new THREE.MeshStandardMaterial({
    color: 0x000000, emissive: 0x00d4ff, emissiveIntensity: 2.0,
    emissiveMap: flowTex, map: flowTex, toneMapped: false,
    transparent: true, opacity: 0.7, depthWrite: false
  });
  const waterFlow = new THREE.Mesh(new THREE.TubeGeometry(spoutCurve, IS_MOBILE ? 32 : 64, 0.018, IS_MOBILE ? 6 : 8, false), flowMat);
  waterFlow.visible = false;
  m.add(waterFlow);

  // Back panel — skip detailed elements on mobile
  const backFaceZ = -halfD;
  if (!IS_MOBILE) {
    const filterCover = new THREE.Mesh(new RoundedBoxGeometry(0.40, 0.72, 0.012, 4, 0.04), chromeMirror);
    filterCover.position.set(-0.30, 0.08, backFaceZ - 0.006);
    m.add(filterCover);
    const filterBorder = new THREE.Mesh(new RoundedBoxGeometry(0.42, 0.74, 0.004, 4, 0.045), brushedSteel);
    filterBorder.position.set(-0.30, 0.08, backFaceZ - 0.014);
    m.add(filterBorder);
    for (const x of [0.18, 0.36]) {
      const kh = new THREE.Mesh(new RoundedBoxGeometry(0.040, 0.072, 0.008, 4, 0.018), recessDark);
      kh.position.set(x, halfH - 0.18, backFaceZ - 0.004);
      m.add(kh);
    }
    const speakerCanvas = document.createElement('canvas');
    speakerCanvas.width = 128; speakerCanvas.height = 128;
    const spk = speakerCanvas.getContext('2d');
    spk.fillStyle = '#0c0c0c'; spk.fillRect(0, 0, 128, 128);
    spk.translate(64, 64);
    spk.strokeStyle = '#3a3a3a'; spk.lineWidth = 2;
    for (let r = 8; r < 60; r += 8) { spk.beginPath(); spk.arc(0, 0, r, 0, Math.PI * 2); spk.stroke(); }
    const speakerTex = new THREE.CanvasTexture(speakerCanvas);
    const speaker = new THREE.Mesh(new THREE.CircleGeometry(0.072, 32), new THREE.MeshStandardMaterial({ map: speakerTex, roughness: 0.7, metalness: 0.2 }));
    speaker.position.set(0.30, 0.04, backFaceZ - 0.002);
    speaker.rotation.y = Math.PI;
    m.add(speaker);
    const powerSwitch = new THREE.Mesh(new THREE.BoxGeometry(0.080, 0.048, 0.014), recessDark);
    powerSwitch.position.set(0.34, -0.18, backFaceZ - 0.007);
    m.add(powerSwitch);
    const rocker = new THREE.Mesh(new THREE.BoxGeometry(0.040, 0.024, 0.008), switchRed);
    rocker.position.set(0.34, -0.18, backFaceZ - 0.014);
    m.add(rocker);
    const fuseHolder = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.012, 18), recessDark);
    fuseHolder.rotation.x = Math.PI / 2;
    fuseHolder.position.set(0.34, -0.32, backFaceZ - 0.006);
    m.add(fuseHolder);
    const fuseCap = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.022, 0.014, 16), fuseBlue);
    fuseCap.rotation.x = Math.PI / 2;
    fuseCap.position.set(0.34, -0.32, backFaceZ - 0.013);
    m.add(fuseCap);
    const fitting = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.024, 16), brushedSteel);
    fitting.rotation.x = Math.PI / 2;
    fitting.position.set(0, -halfH + 0.04, backFaceZ - 0.012);
    m.add(fitting);
    const cableCps = [
      new THREE.Vector3(0,    -halfH + 0.04, backFaceZ - 0.024),
      new THREE.Vector3(0,    -halfH - 0.16, backFaceZ - 0.10),
      new THREE.Vector3(0.10, -halfH - 0.32, backFaceZ - 0.18),
      new THREE.Vector3(0.32, -halfH - 0.40, backFaceZ - 0.16)
    ];
    const cableCurve = new THREE.CatmullRomCurve3(cableCps, false, 'catmullrom', 0.5);
    const cable = new THREE.Mesh(new THREE.TubeGeometry(cableCurve, 32, 0.012, 8, false), cableMat);
    m.add(cable);
  }

  m.userData.dialRotGroup = dialRotGroup;
  m.userData.flowTex = flowTex;
  m.userData.waterFlow = waterFlow;
  return m;
}

function buildMolecule() {
  const g = new THREE.Group();
  const segs = IS_MOBILE ? 24 : 48;
  const atomMat = (color, roughness = 0.18) => IS_MOBILE
    ? new THREE.MeshStandardMaterial({ color, roughness, metalness: 0, emissive: color, emissiveIntensity: 0.15 })
    : new THREE.MeshPhysicalMaterial({ color, roughness, metalness: 0.0, clearcoat: 1, clearcoatRoughness: 0.08, transmission: 0.25, thickness: 1, sheen: 1, sheenColor: color });
  const O = new THREE.Mesh(new THREE.SphereGeometry(0.55, segs, segs), atomMat(BLUE_MID, 0.22));
  g.add(O);
  const angle = (104.5 / 2) * (Math.PI / 180);
  const bondLen = 1.0;
  const H1pos = new THREE.Vector3(Math.sin(angle) * bondLen, -Math.cos(angle) * bondLen, 0);
  const H2pos = new THREE.Vector3(-Math.sin(angle) * bondLen, -Math.cos(angle) * bondLen, 0);
  const H1 = new THREE.Mesh(new THREE.SphereGeometry(0.32, segs, segs), atomMat(BLUE_LIGHT)); H1.position.copy(H1pos); g.add(H1);
  const H2 = new THREE.Mesh(new THREE.SphereGeometry(0.32, segs, segs), atomMat(BLUE_LIGHT)); H2.position.copy(H2pos); g.add(H2);
  const bondMat = new THREE.MeshStandardMaterial({ color: BLUE, roughness: 0.55, metalness: 0.15 });
  function bond(from, to) {
    const dir = new THREE.Vector3().subVectors(to, from);
    const len = dir.length();
    const mid = new THREE.Vector3().addVectors(from, to).multiplyScalar(0.5);
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
    const c = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, len, IS_MOBILE ? 10 : 16), bondMat);
    c.position.copy(mid); c.quaternion.copy(q); g.add(c);
  }
  bond(new THREE.Vector3(0,0,0), H1pos);
  bond(new THREE.Vector3(0,0,0), H2pos);
  return g;
}

const VignetteShader = {
  uniforms: { tDiffuse: { value: null }, offset: { value: 1.10 }, darkness: { value: 0.55 } },
  vertexShader: `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: `uniform sampler2D tDiffuse; uniform float offset; uniform float darkness; varying vec2 vUv;
    void main() {
      vec4 t = texture2D(tDiffuse, vUv);
      vec2 d = (vUv - 0.5) * vec2(offset);
      float v = clamp(1.0 - dot(d, d) * darkness, 0.0, 1.0);
      gl_FragColor = vec4(t.rgb * v, t.a);
    }`
};
const ChromaticShader = {
  uniforms: { tDiffuse: { value: null }, amount: { value: 0.0006 } },
  vertexShader: `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: `uniform sampler2D tDiffuse; uniform float amount; varying vec2 vUv;
    void main() {
      vec2 dir = vUv - 0.5;
      float r = texture2D(tDiffuse, vUv + dir * amount).r;
      vec4 g = texture2D(tDiffuse, vUv);
      float b = texture2D(tDiffuse, vUv - dir * amount).b;
      gl_FragColor = vec4(r, g.g, b, g.a);
    }`
};

function init(container) {
  if (container.parentElement !== document.body) document.body.appendChild(container);
  const overlayEl = document.querySelector('.h2-text-overlay');
  if (overlayEl && overlayEl.parentElement !== document.body) document.body.appendChild(overlayEl);
  const flashEl = document.querySelector('.h2-flash');
  if (flashEl && flashEl.parentElement !== document.body) document.body.appendChild(flashEl);
  const splashEl = document.querySelector('.h2-splash');
  if (splashEl && splashEl.parentElement !== document.body) document.body.appendChild(splashEl);
  canvasContainer = container;

  const canvas = container.querySelector('canvas');
  if (!canvas) return;
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: !IS_MOBILE, alpha: false, powerPreference: 'high-performance' });
  renderer.setClearColor(BG, 1);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, IS_MOBILE ? 1.0 : 1.5));
  renderer.shadowMap.enabled = !IS_MOBILE;
  if (renderer.shadowMap.enabled) renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;

  const scene = new THREE.Scene();
  scene.fog = new THREE.Fog(BG, 9, 24);

  const pmrem = new THREE.PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();
  scene.environment = pmrem.fromScene(new RoomEnvironment(renderer), 0.04).texture;

  const camera = new THREE.PerspectiveCamera(36, 1, 0.1, 60);
  camera.position.set(0, 0, 3.0);

  scene.add(new THREE.HemisphereLight(0x18223a, 0x000000, 0.20));
  const key = new THREE.DirectionalLight(0xffffff, 2.0);
  key.position.set(2, 3, 2);
  if (!IS_MOBILE) {
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    Object.assign(key.shadow.camera, { near: 0.5, far: 25, left: -5, right: 5, top: 5, bottom: -5 });
    key.shadow.bias = -0.0008;
  }
  scene.add(key);
  const rim = new THREE.DirectionalLight(0xa8d4ff, 2.5);
  rim.position.set(-1, 2, -2); scene.add(rim);
  const fill = new THREE.DirectionalLight(0xffe8d4, 0.5);
  fill.position.set(-1, -1, 1); scene.add(fill);
  if (!IS_MOBILE) {
    const showroomSpot = new THREE.SpotLight(0xa8d4ff, 1.5, 14, 0.4, 1.0, 1.0);
    showroomSpot.position.set(-3, 4, 2);
    showroomSpot.target.position.set(0, 0, 0);
    scene.add(showroomSpot); scene.add(showroomSpot.target);
    const cyanL = new THREE.PointLight(CYAN, 5, 14); cyanL.position.set(-3.5, 1.5, 2); scene.add(cyanL);
    const navyL = new THREE.PointLight(BLUE_MID, 3.5, 12); navyL.position.set(3.5, -1, -2); scene.add(navyL);
  }

  // Caustic SpotLight — DESKTOP ONLY
  let causticTex = null;
  if (!IS_MOBILE) {
    causticTex = makeCausticsTexture();
    const causticSpot = new THREE.SpotLight(0xa8d4ff, 1.2, 14, 0.6, 0.8, 1.0);
    causticSpot.position.set(1, 4, 1);
    causticSpot.target.position.set(0, 0, 0);
    causticSpot.map = causticTex;
    scene.add(causticSpot); scene.add(causticSpot.target);
  }

  // Reflective floor — DESKTOP ONLY
  if (!IS_MOBILE) {
    const reflector = new Reflector(new THREE.PlaneGeometry(8, 8), {
      textureWidth: 1024, textureHeight: 1024, color: 0x0a0e1a
    });
    reflector.rotation.x = -Math.PI / 2;
    reflector.position.y = -1.18;
    scene.add(reflector);
  } else {
    const floor = new THREE.Mesh(new THREE.CircleGeometry(8, 32), new THREE.MeshBasicMaterial({ color: 0x06080f }));
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -1.18;
    scene.add(floor);
  }

  const waterMat = IS_MOBILE
    ? new THREE.MeshStandardMaterial({
        color: 0xa8d4ff, roughness: 0.05, metalness: 0,
        transparent: true, opacity: 0.7, depthWrite: false,
        emissive: 0x1a3a5a, emissiveIntensity: 0.15
      })
    : new THREE.MeshPhysicalMaterial({
        color: 0xe8f3ff, roughness: 0.04, transmission: 0.98, thickness: 1.4, ior: 1.38,
        clearcoat: 1.0, clearcoatRoughness: 0.02, attenuationColor: 0xa8d4ff, attenuationDistance: 6.0,
        iridescence: 0.25, iridescenceIOR: 1.3, iridescenceThicknessRange: [120, 580],
        envMapIntensity: 1.6, transparent: true
      });

  const RES = IS_MOBILE ? 16 : 32;
  const droplet = new MarchingCubes(RES, waterMat, true, true, IS_MOBILE ? 30000 : 90000);
  droplet.scale.setScalar(2.0); droplet.isolation = 80;
  scene.add(droplet);

  const bubbleGroup = new THREE.Group();
  const N = IS_MOBILE ? 10 : 26;
  const bubbleSegs = IS_MOBILE ? 10 : 18;
  const bubbles = [];
  for (let i = 0; i < N; i++) {
    const r = 0.07 + Math.random() * 0.18;
    const m = new THREE.Mesh(new THREE.SphereGeometry(r, bubbleSegs, bubbleSegs), waterMat);
    const u = 2 * Math.random() - 1;
    const phi = Math.random() * Math.PI * 2;
    const sx = Math.sqrt(1 - u * u) * Math.cos(phi);
    const sy = u;
    const sz = Math.sqrt(1 - u * u) * Math.sin(phi);
    m.userData = {
      sx, sy, sz,
      dirX: sx * (1.2 + Math.random() * 1.4),
      dirY: sy * (0.6 + Math.random() * 1.0),
      dirZ: sz * (1.2 + Math.random() * 1.4) + (0.4 + Math.random() * 1.0),
      delay: Math.random() * 0.05,
      wobble: Math.random() * Math.PI * 2,
      baseScale: 0.5 + Math.random() * 0.4
    };
    m.position.set(sx * 1.45, sy * 1.45, sz * 1.45);
    m.scale.setScalar(m.userData.baseScale);
    bubbleGroup.add(m); bubbles.push(m);
  }
  scene.add(bubbleGroup);

  function makeContactShadow() {
    const c = document.createElement('canvas'); c.width = 256; c.height = 256;
    const x = c.getContext('2d');
    const grad = x.createRadialGradient(128, 128, 16, 128, 128, 128);
    grad.addColorStop(0, 'rgba(0,0,0,0.65)');
    grad.addColorStop(0.45, 'rgba(0,0,0,0.30)');
    grad.addColorStop(1, 'rgba(0,0,0,0.0)');
    x.fillStyle = grad; x.fillRect(0, 0, 256, 256);
    return new THREE.CanvasTexture(c);
  }
  const contactShadow = new THREE.Mesh(
    new THREE.PlaneGeometry(2.6, 2.6),
    new THREE.MeshBasicMaterial({ map: makeContactShadow(), transparent: true, depthWrite: false, opacity: 0.55 })
  );
  contactShadow.rotation.x = -Math.PI / 2;
  contactShadow.position.y = -1.18;
  contactShadow.renderOrder = 1;
  contactShadow.visible = false;
  scene.add(contactShadow);

  const machine = buildHomeMachine();
  machine.position.y = -5; machine.visible = false;
  scene.add(machine);

  const ledLight = new THREE.PointLight(0x00d4ff, IS_MOBILE ? 0.5 : 1.0, 1.6, 2.0);
  ledLight.position.set(0, -0.75, 0.40);
  machine.add(ledLight);

  const haloMat = new THREE.MeshBasicMaterial({ color: BLUE_MID, transparent: true, opacity: 0, side: THREE.BackSide, depthWrite: false });
  const halo = new THREE.Mesh(new THREE.SphereGeometry(4.5, IS_MOBILE ? 16 : 32, IS_MOBILE ? 16 : 32), haloMat);
  halo.position.set(0, 0, -2.2); scene.add(halo);

  const molecule = buildMolecule();
  molecule.position.set(0, 0, -1.5); molecule.scale.setScalar(0); molecule.visible = false;
  scene.add(molecule);

  // Postprocessing — DESKTOP ONLY
  let composer = null;
  let bloomPass = null;
  if (!IS_MOBILE) {
    composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      0.4, 0.4, 0.85
    );
    composer.addPass(bloomPass);
    composer.addPass(new ShaderPass(VignetteShader));
    composer.addPass(new ShaderPass(ChromaticShader));
    composer.addPass(new OutputPass());
  }

  function resize() {
    const w = window.innerWidth, h = window.innerHeight;
    renderer.setSize(w, h, false);
    if (composer) composer.setSize(w, h);
    if (bloomPass) bloomPass.resolution.set(w, h);
    camera.aspect = w / h; camera.updateProjectionMatrix();
  }
  resize(); window.addEventListener('resize', resize);

  const overlay = document.querySelector('.h2-text-overlay');
  const flash = document.querySelector('.h2-flash');
  const splash = document.querySelector('.h2-splash');

  trackScroll();

  let smooth = 0, t = 0;

  function tick() {
    t += 0.016;
    smooth += (progress - smooth) * 0.08;
    const p = smooth;

    if (causticTex) {
      causticTex.offset.x = (t * 0.02) % 1;
      causticTex.offset.y = (t * 0.013) % 1;
    }

    const shrinkT = Math.max(0, Math.min(1, p / 0.20));
    const dropScale = 2.0 * (1 - shrinkT);
    droplet.visible = dropScale > 0.06;
    if (droplet.visible) {
      droplet.reset();
      const T = t * 0.42;
      const s = 0.5;
      droplet.addBall(0.5 + Math.sin(T * 0.70) * 0.22, 0.5 + Math.cos(T * 0.55) * 0.15, 0.5, s, 12);
      droplet.addBall(0.5 + Math.cos(T * 0.50 + 1.2) * 0.20, 0.5 + Math.sin(T * 0.73 + 0.8) * 0.16, 0.5 + Math.sin(T * 0.30) * 0.05, s * 0.85, 12);
      droplet.addBall(0.5 + Math.sin(T * 0.90 + 2.1) * 0.15, 0.5 + Math.cos(T * 0.60 + 1.5) * 0.18, 0.5 + Math.cos(T * 0.40 + 0.7) * 0.04, s * 0.70, 12);
      droplet.addBall(0.5 + Math.cos(T * 1.10 + 0.5) * 0.11, 0.5 + Math.sin(T * 0.85 + 2.3) * 0.11, 0.5 + Math.sin(T * 0.60) * 0.03, s * 0.55, 12);
      droplet.update();
      droplet.scale.setScalar(dropScale);
      droplet.rotation.y = t * 0.06;
    }

    bubbleGroup.visible = p < 0.50;
    if (bubbleGroup.visible) {
      const surfRadius = 1.45 * (1 - shrinkT);
      bubbles.forEach((b) => {
        const peelP = Math.max(0, Math.min(1, (p - b.userData.delay) / 0.32));
        b.position.set(
          b.userData.sx * surfRadius + b.userData.dirX * peelP * 2.4 + Math.sin(t * 0.6 + b.userData.wobble) * 0.04 * peelP,
          b.userData.sy * surfRadius + b.userData.dirY * peelP * 1.8,
          b.userData.sz * surfRadius + b.userData.dirZ * peelP * 5.5
        );
        b.scale.setScalar(b.userData.baseScale + peelP * 0.5);
      });
    }

    const machVis = p > 0.42 && p < 0.85;
    machine.visible = machVis;
    contactShadow.visible = machVis;
    if (machVis) {
      const mIn = Math.max(0, Math.min(1, (p - 0.42) / 0.10));
      const mOut = Math.max(0, (p - 0.70) / 0.15);
      machine.position.y = -5 + mIn * 4.7 + mOut * 6 + Math.sin(t) * 0.04;
      machine.rotation.y = Math.PI * (-0.1 + (p - 0.42) * 0.7);
      machine.rotation.x = -p * 0.05;
      machine.scale.setScalar((0.55 + mIn * 0.35 - mOut * 0.18) * 1.3);
      haloMat.opacity = mIn * 0.20 * (1 - mOut);
      halo.scale.setScalar(1 + mIn * 0.6);

      const dialAngle = Math.max(0, Math.min(1, mIn * (1 - mOut)));
      if (machine.userData.dialRotGroup) {
        machine.userData.dialRotGroup.rotation.z = -Math.PI * 0.75 + dialAngle * Math.PI * 1.5;
      }
      if (machine.userData.waterFlow) {
        machine.userData.waterFlow.visible = mIn > 0.5 && mOut < 0.5;
      }
      if (machine.userData.flowTex) {
        machine.userData.flowTex.offset.x = (machine.userData.flowTex.offset.x - 0.04) % 1;
      }

      contactShadow.position.x = machine.position.x;
      contactShadow.position.z = machine.position.z;
      contactShadow.position.y = -1.18;
      contactShadow.scale.setScalar(0.7 + mIn * 0.6);
      contactShadow.material.opacity = mIn * 0.55 * (1 - mOut * 0.7);
    } else {
      haloMat.opacity = 0;
      if (machine.userData && machine.userData.waterFlow) machine.userData.waterFlow.visible = false;
    }

    molecule.visible = p > 0.72;
    if (molecule.visible) {
      const mp = Math.max(0, Math.min(1, (p - 0.72) / 0.12));
      molecule.scale.setScalar(mp * 0.95);
      molecule.rotation.y = t * 0.3;
      molecule.rotation.x = Math.sin(t * 0.4) * 0.2;
      molecule.position.y = -0.8 + mp * 1.6;
    }

    let camZ;
    if (p < 0.20)      camZ = 3.0 + (p / 0.20) * 1.5;
    else if (p < 0.42) camZ = 4.5 + ((p - 0.20) / 0.22) * 0.8;
    else if (p < 0.52) camZ = 5.3 - ((p - 0.42) / 0.10) * 1.3;
    else if (p < 0.70) camZ = 4.0 + ((p - 0.52) / 0.18) * 1.2;
    else               camZ = 5.2 + ((p - 0.70) / 0.30) * 1.4;
    camera.position.z = camZ;
    camera.position.y = 0.0 - p * 0.3;
    camera.lookAt(0, 0, 0);

    if (composer) composer.render();
    else renderer.render(scene, camera);

    if (overlay) {
      const op = Math.max(0, Math.min(1, (p - 0.50) / 0.15));
      overlay.style.opacity = (op * 0.60).toFixed(3);
    }
    if (flash) {
      const fp = Math.max(0, 1 - Math.abs(p - 0.42) / 0.03);
      flash.style.opacity = (fp * 0.55).toFixed(3);
    }
    if (splash) {
      const sp = Math.max(0, 1 - Math.abs(p - 0.30) / 0.06);
      splash.style.opacity = sp.toFixed(3);
      splash.classList.toggle('is-on', sp > 0.05);
    }

    requestAnimationFrame(tick);
  }
  tick();
}

const io = new IntersectionObserver((entries) => {
  entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
}, { threshold: 0.18 });

const statIO = new IntersectionObserver((entries) => {
  entries.forEach(e => {
    if (!e.isIntersecting) return;
    const el = e.target;
    const target = parseFloat(el.dataset.num);
    const suffix = el.dataset.suffix || '';
    const decimals = String(el.dataset.num).includes('.') ? 1 : 0;
    const dur = 1500;
    const start = performance.now();
    function step(now) {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = target * eased;
      const display = decimals ? v.toFixed(decimals) : Math.round(v).toLocaleString();
      el.textContent = display + suffix;
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
    statIO.unobserve(el);
  });
}, { threshold: 0.4 });

function boot() {
  const c = document.querySelector('[data-h2home-canvas]');
  if (c) init(c);
  document.querySelectorAll('.h2-fadeup').forEach(el => io.observe(el));
  document.querySelectorAll('.h2-stat-num').forEach(el => statIO.observe(el));
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
